import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ArrowRight, AlertTriangle, ShieldCheck, Repeat,
  Droplets, CheckCircle2, Boxes, Gauge, TrendingUp,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

/* bold-parser */
const B = ({ t, cls = "text-ink font-semibold" }) => (
  <>
    {t.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong>
        : <span key={i}>{s}</span>
    )}
  </>
);

/* illustrative asset metrics */
const UTIL_BEFORE = 45;
const UTIL_AFTER = 80;
const FOOTPRINT_CUT = 30;
const TURN_MULT = "3×";
const WASHDOWN = "< 15 min";

const PAINS = [
  "**Cracked composite mats.** Every load. Every season.",
  "**Replacement line-items** never stop.",
  "**Landfill disposal fees** keep stacking.",
  "**Single-terrain mats** sit idle in the yard.",
  "Capital **locked in inventory** you can't deploy.",
];

const LONGEVITY = [
  "Won't crack under **heavy tracking.**",
  "**UV & chemical resistant.**",
  "**Zero replacements** across multi-year footprints.",
  "Replacement line-item: **deleted.**",
];

const UTILIZATION = [
  "**Dual-sided tread.** Fully reversible.",
  "**Tracked excavators today.** Rubber-tired trucks tomorrow.",
  "Moves **terrain to terrain** instantly.",
  "Mats stay **deployed — not stored.**",
];

export default function AssetManagerBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Asset Manager Brief · One-Page</span>
            <a href="/big-bad-mats-asset-roi.pdf" download data-testid="download-asset-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-asset-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="asset-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">For the Equipment / Asset Manager · Heavy Civil Infrastructure</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[33px]">
                End the Replacement Bleed. Empty the Idle Yard.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                Stop measuring <span className="font-semibold text-ink">unit cost.</span> Start measuring <span className="text-flame font-semibold">asset utilization</span> and <span className="text-flame font-semibold">zero replacement cost.</span> One mat. Every terrain. Years of service.
              </p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3">
              <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">The shift</div>
              <div className="mt-1 flex items-center gap-2 font-display font-bold text-[13px]">
                <span className="line-through text-white/40">Unit Cost</span>
                <ArrowRight className="h-4 w-4 text-flame" />
                <span>Asset ROI</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="font-display font-extrabold text-flame text-[20px] leading-none">0</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">replacement line-items</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-flame text-[20px] leading-none">−{FOOTPRINT_CUT}%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">inventory footprint</div>
                </div>
              </div>
            </div>
          </header>

          {/* body: pain (left) + solutions (right) */}
          <div className="grid grid-cols-5 gap-4 pt-4">
            {/* pain */}
            <div className="col-span-2 border border-border flex flex-col">
              <div className="bg-charcoal text-white px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-flame" />
                <span className="font-display font-bold text-[12px] uppercase tracking-wider">The Daily Bleed</span>
              </div>
              <ul className="p-4 space-y-2.5 flex-1">
                {PAINS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug">
                    <span className="mt-1.5 h-1.5 w-1.5 bg-charcoal/40 flex-shrink-0" />
                    <span><B t={p} cls="text-ink font-semibold" /></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* solutions */}
            <div className="col-span-3 grid grid-rows-2 gap-4">
              <div className="border-2 border-flame flex flex-col">
                <div className="bg-flame text-white px-4 py-2 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  <span className="font-display font-bold text-[12px] uppercase tracking-wider">Longevity — Kill Replacements</span>
                </div>
                <ul className="p-3 grid grid-cols-2 gap-x-4 gap-y-2 flex-1 content-center">
                  {LONGEVITY.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug">
                      <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                      <span><B t={x} /></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-2 border-flame flex flex-col">
                <div className="bg-flame text-white px-4 py-2 flex items-center gap-2">
                  <Repeat className="h-4 w-4" />
                  <span className="font-display font-bold text-[12px] uppercase tracking-wider">High Utilization — Always Deployed</span>
                </div>
                <ul className="p-3 grid grid-cols-2 gap-x-4 gap-y-2 flex-1 content-center">
                  {UTILIZATION.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug">
                      <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                      <span><B t={x} /></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* asset ROI breakout */}
          <div className="mt-4 bg-ink text-white flex-1 flex flex-col justify-center px-6 py-4">
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-flame" />
              <span className="font-display font-bold text-[13px] uppercase tracking-wider">The Asset ROI Metric — Turn Faster, Own Less</span>
            </div>
            {/* flow */}
            <div className="mt-3 grid grid-cols-4 gap-2 items-stretch">
              {[
                { icon: Droplets, t: "Non-porous surface", d: "Nothing soaks in. Fully sealed." },
                { icon: TrendingUp, t: `Washdown ${WASHDOWN}`, d: "Decon in minutes, not hours." },
                { icon: Repeat, t: `${TURN_MULT} faster turns`, d: "Site-to-site, back in service." },
                { icon: Boxes, t: `−${FOOTPRINT_CUT}% footprint`, d: "Fewer mats cover the same work." },
              ].map((s, i) => (
                <div key={s.t} className="relative bg-white/5 border border-white/10 p-3">
                  <s.icon className="h-5 w-5 text-flame" />
                  <div className="mt-2 font-display font-extrabold text-[14px] leading-tight text-white">{s.t}</div>
                  <div className="text-white/55 text-[9.5px] mt-0.5 leading-snug">{s.d}</div>
                  {i < 3 && <ArrowRight className="hidden md:block absolute -right-[11px] top-1/2 -translate-y-1/2 h-4 w-4 text-flame z-10" />}
                </div>
              ))}
            </div>
            {/* utilization bar */}
            <div className="mt-4 grid grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="flex justify-between text-[9px] text-white/60 mb-1">
                  <span>Fleet utilization</span>
                  <span className="font-mono">{UTIL_BEFORE}% → <span className="text-flame font-semibold">{UTIL_AFTER}%</span></span>
                </div>
                <div className="h-3 bg-white/10 overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-white/25" style={{ width: `${UTIL_BEFORE}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-flame" style={{ width: `${UTIL_AFTER}%`, clipPath: `inset(0 0 0 ${UTIL_BEFORE}%)` }} />
                </div>
              </div>
              <div className="text-right">
                <div className="text-[8px] uppercase tracking-wider text-white/50">Same operational footprint</div>
                <div className="font-display font-extrabold text-flame text-[22px] leading-none">Fewer assets owned</div>
              </div>
            </div>
          </div>

          {/* CTA footer */}
          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px] leading-tight">Know your fleet's real utilization number.</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center gap-1.5 bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">
              Request an Asset Utilization &amp; ROI Assessment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Utilization &amp; footprint figures are illustrative estimates — confirmed against your fleet during the assessment. Not a quote, guarantee, or warranty of any specific result; not financial, engineering, or legal advice. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
